import { z } from "zod"

import { seasonSchema } from "./season"
import { tariffRateBandSchema } from "./tariff-rate-band"
import { timeOfUseSchema } from "./time-of-use"

// Definition of the TariffRate schema
export const tariffRateSchema = z.object({
  /** Unique Genability ID for each tariff rate */
  tariffRateId: z.number().optional(),
  /** Associates the rate with a tariff */
  tariffId: z.number().optional(),
  /** Tariff ID of the rider attached to this tariff version (nullable) */
  riderTariffId: z.number().nullable().optional(),
  /** Master Tariff ID of the rider linked to this tariff rate */
  riderId: z.number().optional(),
  /** Sequence of this rate in the tariff for display purposes only */
  tariffSequenceNumber: z.number().optional(),
  /** Sequence of this rate in the tariff source document,
   * if it differs from tariffSequenceNumber (nullable) */
  tariffBookSequenceNumber: z.number().nullable().optional(),
  /** Name of the group this rate belongs to */
  rateGroupName: z.string().optional(),
  /** Name of the group this rate belongs to in the tariff
   * source document, if it differs from rateGroupName (nullable) */
  tariffBookRateGroupName: z.string().nullable().optional(),
  /** Name of this rate. Can be null (in which case use the group name) */
  rateName: z.string().nullable(),
  /** Name of this rate in the tariff source document,
   * if it differs from rateName (nullable) */
  tariffBookRateName: z.string().nullable().optional(),
  /** Rates effective date if different from the tariff's effective date */
  fromDateTime: z.string().nullable(),
  /** Rates end date if different from the tariff's end date */
  toDateTime: z.string().nullable(),
  /** The season this rate applies to (nullable) */
  season: seasonSchema.nullable().optional(),
  /** The time period this rate applies to (nullable) */
  timeOfUse: timeOfUseSchema.nullable().optional(),
  /** Possible values for chargeType */
  chargeType: z
    .enum([
      `FIXED_PRICE`,
      `CONSUMPTION_BASED`,
      `DEMAND_BASED`,
      `QUANTITY`,
      `FORMULA`,
      `MINIMUM`,
      `MAXIMUM`,
      `TAX`,
      `NET_EXCESS_GENERATION`,
    ])
    .nullable(),
  /** Comma-separated string indicating the class(es) of
   * charges this rate is for */
  chargeClass: z.string().optional(),
  /** Period for which this charge is calculated */
  chargePeriod: z.enum([`HOURLY`, `MONTHLY`, `DAILY`, `QUARTERLY`, `ANNUALLY`]),
  /** Indicates whether this rate is BUY, SELL, NET,
   * BUY_IMPORT, or SELL_EXPORT */
  transactionType: z
    .enum([`BUY`, `SELL`, `NET`, `BUY_IMPORT`, `SELL_EXPORT`])
    .nullable()
    .optional(),
  /** The property that defines the type of quantity this
   * rate applies to (nullable) */
  quantityKey: z.string().nullable().optional(),
  /** The property that defines the eligibility
   * criteria for this rate (nullable) */
  applicabilityKey: z.string().nullable().optional(),
  hasApplicabilityFormula: z.boolean().optional(),
  territory: z.unknown().optional(),
  /** The variable that determines the upper limit(s) of this rate (nullable) */
  variableLimitKey: z.string().nullable().optional(),
  /** The property that defines the variable rate (nullable) */
  variableRateKey: z.string().nullable().optional(),
  /** The property that defines the variable
   * factor to apply to this rate (nullable) */
  variableFactorKey: z.string().nullable().optional(),
  /** List of TariffRateBand objects */
  rateBands: z.array(tariffRateBandSchema),
})

export type TariffRate = z.infer<typeof tariffRateSchema>
